// src/components/DynamicCard.tsx
"use client";

import { useEffect, useState } from "react";
import { AxiosPost } from "@/utilities/axioscall";
import { AK } from "@/constants";
import DOMPurify from "dompurify";
import Image from "next/image";
import { notFound } from "next/navigation";
import toast, { Toaster } from "react-hot-toast";
import FadeLoader from "react-spinners/FadeLoader";

  const storeCode = process.env.NEXT_PUBLIC_STORE_CODE
  const assetsurl =
    `https://assetsvilva.blr1.cdn.digitaloceanspaces.com/ecomsaas/redisapi/${storeCode}`;

interface DynamicCardProps {
  pageslug: string;
  pagetitle: string;
  pagecontent: string;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  image?: string; // Optional for blog posts
}

const DynamicCard = ({
  pageslug,
  pagetitle,
  pagecontent,
  meta_title,
  meta_description,
  meta_keywords,
  image,
}: DynamicCardProps) => {
  const [formValues, setFormValues] = useState({
    firstName: "",
    email: "",
    phone: "",
    about: "",
    firstNameError: false,
    emailError: false,
    phoneError: false,
    aboutError: false,
    formloading: false,
  });

  // Sanitize HTML content in the browser
  const sanitizedContent = typeof window !== "undefined" ? DOMPurify.sanitize(pagecontent) : pagecontent;

  useEffect(() => {
    if (!pagecontent) {
      notFound();
    }
  }, [pagecontent]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    let error = false;

    if (formValues.firstName === "") {
      setFormValues((prev) => ({ ...prev, firstNameError: true }));
      error = true;
    } else {
      setFormValues((prev) => ({ ...prev, firstNameError: false }));
    }

    if (formValues.email === "") {
      setFormValues((prev) => ({ ...prev, emailError: true }));
      error = true;
    } else {
      setFormValues((prev) => ({ ...prev, emailError: false }));
    }

    if (formValues.phone === "") {
      setFormValues((prev) => ({ ...prev, phoneError: true }));
      error = true;
    } else {
      setFormValues((prev) => ({ ...prev, phoneError: false }));
    }

    if (formValues.about === "") {
      setFormValues((prev) => ({ ...prev, aboutError: true }));
      error = true;
    } else {
      setFormValues((prev) => ({ ...prev, aboutError: false }));
    }

    if (error) return;

    setFormValues((prev) => ({ ...prev, formloading: true }));

    const formdata = {
      name: formValues.firstName,
      email: formValues.email,
      mobile: formValues.phone,
      message: formValues.about,
    };

    try {
      const res = await AxiosPost(AK.SUBMITCONTACTFORMAPI, formdata);
      if (typeof res !== "string" && res?.data?.success) {
        toast.success("Contact form submitted successfully.", {
          style: { border: "1px solid #00b852", padding: "16px", color: "#00b852" },
          iconTheme: { primary: "#00b852", secondary: "#FFFAEE" },
        });
        setFormValues({
          firstName: "",
          email: "",
          phone: "",
          about: "",
          firstNameError: false,
          emailError: false,
          phoneError: false,
          aboutError: false,
          formloading: false,
        });
      } else {
        toast.error("Contact form submission failed.", {
          style: { border: "1px solid #cc0000", padding: "16px", color: "#cc0000" },
          iconTheme: { primary: "#cc0000", secondary: "#FFFAEE" },
        });
      }
    } catch (error) {
      toast.error("Contact form submission error.", {
        style: { border: "1px solid #cc0000", padding: "16px", color: "#cc0000" },
        iconTheme: { primary: "#cc0000", secondary: "#FFFAEE" },
      });
    } finally {
      setFormValues((prev) => ({ ...prev, formloading: false }));
    }
  };

  return (
    <>
      <Toaster />
      {pageslug !== "contact-us" && (
        <section className="container-y bg-white py-4">
          <div className="container mx-auto px-12">
            <div className="-mx-3 mb-2 flex flex-wrap">
              {pagetitle && (
                <>
                  <br />
                  <h1 className="text-center text-3xl font-bold uppercase" style={{ width: "100%" }}>
                    {pagetitle}
                  </h1>
                  <br />
                  <br />
                  {image && (
                    <Image
                      src={`${assetsurl}${image}`}
                      alt={pagetitle || "Page Image"}
                      width={800}
                      height={400}
                      className="w-full h-auto object-cover mb-4"
                    />
                  )}
                  <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {pageslug === "contact-us" && (
        <section className="container-y bg-white py-4 min-h-[85vh]">
          <div className="container mx-auto mt-10 px-4">
            <div className="flex flex-col-reverse flex-wrap justify-between sm:flex-row">
              <div className="mb-4 w-full sm:w-1/2">
                <div className="max-w-full p-2">
                  {pagetitle && (
                    <>
                      <h1 className="font-heading mb-4 text-3xl font-bold">{pagetitle}</h1>
                      <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
                    </>
                  )}
                </div>
              </div>
              <div className="w-full sm:w-1/2">
                <form>
                  <div className="space-y-12">
                    <div className="border-b border-gray-900/10 pb-12">
                      <p className="lg:mt-0 md:mt-0 sm:mt-5 -mt-7 text-sm leading-6 text-gray-600">
                        Should you have any queries or require assistance, feel free to reach out to us at any time – we're here to help.
                      </p>
                      <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div className="col-span-full sm:col-span-3">
                          <label htmlFor="firstName" className="block text-sm font-medium leading-6 text-gray-900">
                            Name *
                          </label>
                          <div className="mt-2">
                            <input
                              type="text"
                              name="firstName"
                              id="firstName"
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              value={formValues.firstName}
                              onChange={handleChange}
                            />
                            {formValues.firstNameError && (
                              <p className="mb-2 text-red-500">Please enter your name.</p>
                            )}
                          </div>
                        </div>
                        <div className="col-span-full sm:col-span-3">
                          <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                            Email address *
                          </label>
                          <div className="mt-2">
                            <input
                              id="email"
                              name="email"
                              type="email"
                              autoComplete="email"
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              value={formValues.email}
                              onChange={handleChange}
                            />
                            {formValues.emailError && (
                              <p className="mb-2 text-red-500">Please enter your email.</p>
                            )}
                          </div>
                        </div>
                        <div className="col-span-full sm:col-span-3">
                          <label htmlFor="phone" className="block text-sm font-medium leading-6 text-gray-900">
                            Phone Number *
                          </label>
                          <div className="mt-2">
                            <input
                              type="text"
                              name="phone"
                              id="phone"
                              autoComplete="tel"
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              value={formValues.phone}
                              onChange={handleChange}
                            />
                            {formValues.phoneError && (
                              <p className="mb-2 text-red-500">Please enter your phone.</p>
                            )}
                          </div>
                        </div>
                        <div className="col-span-full">
                          <label htmlFor="about" className="block text-sm font-medium leading-6 text-gray-900">
                            How can I assist you today? *
                          </label>
                          <div className="mt-2">
                            <textarea
                              id="about"
                              name="about"
                              rows={3}
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              value={formValues.about}
                              onChange={handleChange}
                            />
                            {formValues.aboutError && (
                              <p className="mb-2 text-red-500">Please enter how I can assist you.</p>
                            )}
                          </div>
                          <p className="mt-3 text-sm leading-6 text-gray-600">
                            I'm here to assist you with any questions or tasks you have on your mind. Feel free to let me know how I can help.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-end gap-x-6">
                    <button
                      type="button"
                      className="text-sm font-semibold leading-6 text-gray-900"
                      onClick={() => setFormValues({
                        firstName: "",
                        email: "",
                        phone: "",
                        about: "",
                        firstNameError: false,
                        emailError: false,
                        phoneError: false,
                        aboutError: false,
                        formloading: false,
                      })}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rounded-md !bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      onClick={handleSubmit}
                    >
                      Send
                    </button>
                    <FadeLoader
                      loading={formValues.formloading}
                      width={2}
                      height={10}
                      color="rgb(84 117 229)"
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default DynamicCard;